import { Controller } from '@hotwired/stimulus';

// Blind head-to-head on /benchmark: two answers to one task, pick one, then reveal the models.
export default class extends Controller {
  static targets = ['card'];
  static values = { pairUrl: String, voteUrl: String };

  connect() {
    this.load();
  }

  async load() {
    this.cardTarget.innerHTML = '<p class="text-stone-500">Loading a matchup…</p>';
    try {
      const res = await fetch(this.pairUrlValue, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      this.pair = await res.json();
    } catch {
      this.cardTarget.innerHTML = '<p class="text-stone-500">Couldn\'t load a matchup. Try again in a bit.</p>';
      return;
    }
    if (this.pair.done) {
      this.cardTarget.innerHTML = '<p class="text-stone-700">You\'ve graded every matchup we have. Thank you! Add your own result below to make more.</p>';
      return;
    }
    this.render();
  }

  render() {
    const { task, a, b } = this.pair;
    this.cardTarget.innerHTML = `
      <p class="text-xs font-medium text-stone-500">${esc(task.category)}</p>
      <h3 class="text-lg font-bold text-stone-950">${esc(task.title)}</h3>
      ${task.sample_data ? `<p class="mt-2 text-sm text-stone-600">${esc(task.sample_data)}</p>` : ''}
      <p class="mt-1 text-sm font-medium text-stone-800">${esc(task.prompt)}</p>
      <div class="mt-5 grid gap-4 md:grid-cols-2">
        ${this.answer('A', a)}
        ${this.answer('B', b)}
      </div>
      <div class="mt-5 flex flex-wrap gap-2" data-role="choices">
        ${this.button('a', 'A is better', true)}
        ${this.button('b', 'B is better', true)}
        ${this.button('both', 'Both fine')}
        ${this.button('neither', 'Neither works')}
      </div>`;
  }

  answer(letter, run) {
    return `<div class="rounded-xl border border-stone-200" data-answer="${letter.toLowerCase()}">
      <div class="flex items-center justify-between border-b border-stone-100 px-3 py-2 text-sm font-semibold text-stone-700">
        <span>Answer ${letter}</span><span data-reveal class="font-normal text-stone-500"></span>
      </div>
      <pre class="max-h-64 overflow-auto whitespace-pre-wrap break-all p-3 font-mono text-[13px] leading-relaxed text-stone-900">${esc(run.output)}</pre>
    </div>`;
  }

  button(choice, label, primary = false) {
    const cls = primary
      ? 'bg-[#188038] text-white hover:bg-[#136b2e]'
      : 'border border-stone-300 bg-white text-stone-800 hover:bg-stone-50';
    return `<button type="button" class="rounded-md px-4 py-2 text-sm font-semibold ${cls}" data-action="benchmark-grade#vote" data-choice="${choice}">${label}</button>`;
  }

  async vote(event) {
    const choice = event.currentTarget.dataset.choice;
    this.cardTarget.querySelectorAll('[data-role=choices] button').forEach((btn) => { btn.disabled = true; });
    const body = new URLSearchParams({ a: this.pair.a.id, b: this.pair.b.id, choice });
    const res = await fetch(this.voteUrlValue, { method: 'POST', body, credentials: 'same-origin' });
    if (!res.ok) {
      this.cardTarget.querySelector('[data-role=choices]').insertAdjacentHTML('afterend', '<p class="mt-3 text-sm text-red-700">That vote didn\'t go through. Refresh and try again.</p>');
      return;
    }
    const result = await res.json();
    this.reveal('a', result.a, result.a_passed, choice === 'a');
    this.reveal('b', result.b, result.b_passed, choice === 'b');
    this.cardTarget.querySelector('[data-role=choices]').innerHTML =
      '<button type="button" class="rounded-md bg-stone-900 px-4 py-2 text-sm font-semibold text-white hover:bg-stone-800" data-action="benchmark-grade#load">Next matchup →</button>';
  }

  reveal(letter, name, passed, picked) {
    const box = this.cardTarget.querySelector(`[data-answer=${letter}]`);
    const verdict = passed == null ? '' : passed ? ' · passed ✓' : ' · failed ✗';
    box.querySelector('[data-reveal]').textContent = `${name}${verdict}`;
    if (picked) box.classList.add('border-[#188038]', 'ring-2', 'ring-[#188038]/20');
  }
}

function esc(text) {
  const div = document.createElement('div');
  div.textContent = text ?? '';
  return div.innerHTML;
}
